// ============================================================
// 単位変換ユーティリティ(原則17: 単位の二層構造)
// ============================================================
// 内部単位: cm / m / mm 自由
// 外部単位: m or m² に統一(設定でカテゴリ別変更可)
// クロス幅 0.9m(関東標準)/ 関西業者は設定変更可
// ============================================================

import type { UnitPreferences } from '@/types';

export type LengthUnit = 'mm' | 'cm' | 'm';
export type AreaUnit = 'sqm' | 'sqcm';

/**
 * 任意の長さ単位を m に変換
 */
export function toMeters(value: number, unit: string): number {
  const u = unit.toLowerCase().replace(/[\s_-]/g, '');
  switch (u) {
    case 'mm':
      return value / 1000;
    case 'cm':
      return value / 100;
    case 'm':
    case '':
      return value;
    default:
      // 不明な単位はそのまま返す(警告は呼び出し側で)
      return value;
  }
}

/**
 * 長さ(m)からクロス面積(m²)に変換
 * クロス幅は設定値(関東 0.9m / 関西 0.92m など)
 */
export function lengthToWallpaperArea(lengthM: number, prefs: UnitPreferences): number {
  return lengthM * prefs.wallpaper_width_m;
}

/**
 * クロス用: 内部単位を踏まえて外部単位に変換
 * 出力単位が m なら長さで、sqm なら面積で返す
 */
export function convertForWallpaperOutput(
  value: number,
  inputUnit: string,
  prefs: UnitPreferences,
): { value: number; unit: 'm' | 'sqm' } {
  const meters = toMeters(value, inputUnit);
  if (prefs.wallpaper_output === 'm') {
    return { value: roundTo(meters, 2), unit: 'm' };
  }
  return { value: roundTo(lengthToWallpaperArea(meters, prefs), 2), unit: 'sqm' };
}

/**
 * 床材用
 */
export function convertForFlooringOutput(
  value: number,
  inputUnit: string,
  prefs: UnitPreferences,
): { value: number; unit: 'm' | 'sqm' } {
  const meters = toMeters(value, inputUnit);
  // 床材は長尺ものと面積ものがあるため、設定に従う
  if (prefs.flooring_output === 'm') {
    return { value: roundTo(meters, 2), unit: 'm' };
  }
  return { value: roundTo(meters * meters, 2), unit: 'sqm' };
}

/**
 * 小数点丸め
 */
export function roundTo(value: number, digits: number): number {
  const m = Math.pow(10, digits);
  return Math.round(value * m) / m;
}

/**
 * 単位の表示(UI 用)
 */
export function formatUnit(unit: string): string {
  if (unit === 'sqm') return '㎡';
  if (unit === 'sqcm') return '㎠';
  return unit;
}

/**
 * デフォルト単位設定(関東標準)
 */
export function defaultUnitPreferences(): UnitPreferences {
  return {
    wallpaper_output: 'sqm',
    flooring_output: 'sqm',
    length_output: 'm',
    wallpaper_width_m: 0.9,
    internal_freedom: true,
  };
}
